import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, Link } from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import type { ResetPasswordRequest } from '@/types/auth';
import { styled } from '@mui/material/styles';
import { Button, Typography, Input } from '@/components/ui';
import { clearAuthCookiesService } from '@/services/clearAuthCookiesService';
import toast from 'react-hot-toast';

const BRAND_COLOR = '#17A6DA';

// Styled components matching Regions design (reusing the same styling as LoginForm)
const LoginContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 40%',
  padding: theme.spacing(2, 4),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: 'white',
  [theme.breakpoints.down('md')]: {
    flex: '1',
    padding: theme.spacing(4, 3),
  },
  paddingBottom: '100px',
}));

const RightPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 60%',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 200px',
  },
}));

// Login Banner styled components
const BannerContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  //background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
}));

const BannerImage = styled('img')(() => ({
  width: '100%',
  height: '100%',

  objectPosition: 'cover',
}));

const FormCard = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  padding: theme.spacing(6, 4),
  borderRadius: '12px',
  boxShadow: `
  0 8px 16px rgba(0,0,0,0.08),
  0 20px 40px rgba(0,0,0,0.15)
`,
  border: '1px solid #f0f0f0',
}));

const BrandHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(4),
  gap: theme.spacing(1.5),
}));

const WelcomeSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const FormSection = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

const FooterLinks = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(2),
}));

const LogoHeader = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(3),
  left: theme.spacing(4),
  // [theme.breakpoints.down('sm')]: {
  //   left: '50%',
  //   transform: 'translateX(-50%)',
  // },
}));

const inputSx = {
  '& .MuiInputBase-root': {
    backgroundColor: 'transparent !important',
  },

  '& .MuiOutlinedInput-root': {
    backgroundColor: 'transparent !important',
    borderRadius: '8px',

    '& fieldset': {
      borderColor: '#d0d5dd',
    },

    '&:hover fieldset': {
      borderColor: '#808080',
    },

    '&.Mui-focused fieldset': {
      borderColor: ' #808080',
      borderWidth: '2px',
    },
  },

  // Remove browser autofill bg
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 1000px transparent inset',
    WebkitTextFillColor: '#000',
    transition: 'background-color 9999s ease-in-out 0s',
  },

  // Label colors
  '& .MuiInputLabel-root': {
    color: '#667085',
  },

  '& .MuiInputLabel-root.Mui-focused': {
    color: BRAND_COLOR,
  },
};

// Validation schema
const resetPasswordSchema = yup.object({
  password: yup
    .string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character'
    ),
  confirmPassword: yup
    .string()
    .required('Confirm password is required')
    .oneOf([yup.ref('password')], 'Passwords must match'),
});

interface ResetPasswordFormProps {
  token: string;
}

export const ResetPasswordForm: React.FC<ResetPasswordFormProps> = ({ token }) => {
  const navigate = useNavigate();
  const { resetPassword, loading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Initial form values
  const initialValues: Omit<ResetPasswordRequest, 'token'> = {
    password: '',
    confirmPassword: '',
  };

  // Handle form submission
  const handleSubmit = async (
    values: Omit<ResetPasswordRequest, 'token'>,
    { setFieldError }: { setFieldError: (field: string, message: string) => void }
  ) => {
    const loadingToast = toast.loading('Welcome back! Getting everything ready for you…', {
      position: 'top-center',
      icon: (
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/c/c7/Loading_2.gif?20170503175831"
          alt="loading"
          style={{
            width: '50px', // change size here
            height: '50px',
            objectFit: 'contain',
            marginRight: '8px',
          }}
        />
      ),
      style: {
        marginTop: '40vh',
        transform: 'translateY(-50%)',

        background: '#ffffff',
        color: '#000',
        padding: '18px 24px',
        borderRadius: '12px',
        width: '620px',
        textAlign: 'center',

        borderTop: '4px solid #17A6DA',
        fontSize: '18px',
        fontWeight: 500,
        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
      },
    });
    try {
      await resetPassword({
        token,
        password: values.password,
        confirmPassword: values.confirmPassword,
      });
      toast.dismiss(loadingToast);
    } catch (error) {
      toast.dismiss(loadingToast);
      const errorMessage = error instanceof Error ? error.message : 'Password reset failed';
      setFieldError('password', errorMessage);
    }
  };

  return (
    <LoginContainer>
      <LogoHeader>
        <img
          src="/logos/logo-white_1.png"
          alt="GrowthBnk Logo"
          style={{ width: '150px', objectFit: 'contain' }}
        />
      </LogoHeader>
      {/* Left Panel - Reset Password Form */}
      <LeftPanel>
        {/* Brand Header - Regions Style */}

        <FormSection>
          {/* Welcome Section - Regions Style */}
          <WelcomeSection>
            <Typography
              variant="h1"
              weight="bold"
              color="textPrimary"
              textAlign={'center'}
              sx={{ fontSize: { xs: '1.00rem', sm: '1.5rem' }, color: 'rgb(40, 42, 48)' }}
            >
              Reset Password
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign={'center'}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}
            >
              Enter your new password to reset your account.
            </Typography>
          </WelcomeSection>

          {/* Reset Password Form */}
          <Box
            sx={{
              width: '70%',
              margin: '0 auto',
            }}
          >
            <FormCard>
              <Formik
                initialValues={initialValues}
                validationSchema={resetPasswordSchema}
                onSubmit={handleSubmit}
              >
                {({ values, errors, touched, setFieldValue, setFieldTouched }) => (
                  <Form>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {/* New Password Field */}
                      <Box>
                        <Input
                          fullWidth
                          label="New Password"
                          name="password"
                          type="password"
                          placeholder="Enter New Password"
                          value={values.password}
                          onChange={e => setFieldValue('password', e.target.value)}
                          onBlur={() => setFieldTouched('password', true)}
                          onFocus={() => {
                            if (!touched.password) {
                              setFieldTouched('password', true);
                            }
                          }}
                          error={touched.password && !!errors.password}
                          helperText={touched.password && errors.password}
                          disabled={loading}
                          size="small"
                          variant="outlined"
                          showPassword={showPassword}
                          onPasswordToggle={() => setShowPassword(!showPassword)}
                          sx={inputSx}
                        />
                      </Box>

                      {/* Confirm Password Field */}
                      <Box>
                        <Input
                          fullWidth
                          label="Confirm Password"
                          name="confirmPassword"
                          type="password"
                          placeholder="Confirm New Password"
                          value={values.confirmPassword}
                          onChange={e => setFieldValue('confirmPassword', e.target.value)}
                          onBlur={() => setFieldTouched('confirmPassword', true)}
                          onFocus={() => {
                            if (!touched.confirmPassword) {
                              setFieldTouched('confirmPassword', true);
                            }
                          }}
                          error={touched.confirmPassword && !!errors.confirmPassword}
                          helperText={touched.confirmPassword && errors.confirmPassword}
                          disabled={loading}
                          size="small"
                          variant="outlined"
                          showPassword={showConfirmPassword}
                          onPasswordToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                          sx={inputSx}
                        />
                      </Box>

                      {/* Reset Button */}
                      <Button
                        type="submit"
                        variant="primary"
                        size="small"
                        fullWidth
                        loading={loading}
                        loadingText="Resetting password..."
                        disabled={!values.password || !values.confirmPassword}
                         sx={{
                          color: '#fff',
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: '8px',
                          height: '50px',
                          '&:hover': {
                            backgroundColor: '#1392C1', // slightly darker
                          },
                          '&:disabled': {
                            backgroundColor: '#9bd3ea',
                            color: '#fff',
                          },
                        }}
                      >
                        Reset Password
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </FormCard>
          </Box>

          {/* Footer Links */}
          <FooterLinks>
            <Link
              component={RouterLink}
              to="/auth/login"
              variant="body2"
              sx={{
                color: 'rgb(40, 42, 48)',
                textDecoration: 'none',
                fontSize: '0.75rem',
                '&:hover': {
                  textDecoration: 'none',
                },
              }}
            >
              Back to SignIn
            </Link>
          </FooterLinks>
        </FormSection>
      </LeftPanel>

      {/* Right Panel - Banner */}
      <RightPanel>
        <BannerContainer>
          <BannerImage src="/banner.svg" alt="Reset Password Banner" />
        </BannerContainer>
      </RightPanel>
    </LoginContainer>
  );
};

// This component extracts the token from the URL and passes it to the form
const ResetPasswordPage = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  let token = searchParams.get('token') || '';
  return <ResetPasswordForm token={token} />;
};

export default ResetPasswordPage;
